import React from "react";
import { Badge, Button, Tag, Tooltip } from "antd";
import {
  EditFilled,
  CheckCircleFilled,
  CloseCircleFilled,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import AdsClickOutlinedIcon from "@mui/icons-material/AdsClickOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import MarkunreadMailboxOutlinedIcon from "@mui/icons-material/MarkunreadMailboxOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import ScheduleSendOutlinedIcon from "@mui/icons-material/ScheduleSendOutlined";
import Typography from "app/shared/Typography";
import moment from "moment";
import {
  GST_STATE_CODES,
  STATE_ABBR_TO_GST_CODE,
  getGstCodeFromAbbr,
  getStateCodeFromGstin,
} from "../../utils/GstStateCode";
import { filter } from "lodash";

// Format timestamp into readable date
const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  return moment.unix(timestamp).format("DD-MM-YYYY, hh:mm A"); // Fix: Corrected minute formatting
};

// Booking/check-in/check-out dates sometimes arrive as "YYYY-DD-MMT..."
// (day/month swapped) instead of proper ISO "YYYY-MM-DDT...", which makes
// `new Date()` return Invalid Date whenever the day is > 12. Detect the
// swap (month part > 12) and correct it, in addition to handling plain
// "DD-MM-YYYY" strings, before falling back to native parsing.
const parseBookingDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== "string") {
    value = String(value);
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [day, month, year] = value.split("-");
    const date = new Date(`${year}-${month}-${day}`);
    return isNaN(date.getTime()) ? null : date;
  }

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(T.*)?$/);
  if (isoMatch) {
    const [, year, part2, part3, rest = ""] = isoMatch;
    const [month, day] = Number(part2) > 12 ? [part3, part2] : [part2, part3];
    const date = new Date(`${year}-${month}-${day}${rest}`);
    return isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

const handleInvoiceView = async (blobUrls) => {
  const newTabs = blobUrls.map(() => window.open("", "_blank"));

  try {
    const response = await fetch(
      "https://stage-apiplatform.finkraftai.com/api/storage/objects/azure/file/url",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ blobUrls }),
      },
    );

    const data = await response.json();

    if (data && Array.isArray(data.signedUrls)) {
      data.signedUrls.forEach((url, index) => {
        if (newTabs[index]) {
          newTabs[index].location.href = url;
        }
      });
    } else {
      console.error("No URLs returned from API", data);
    }
  } catch (error) {
    console.error("Failed to fetch invoice URLs:", error);
  }
};


const colorOfEmailEvent = {
  processed: "#007BFF",
  delivered: "#007BFF",
  open: "#28A745",
  click: "#FFC107",
  bounce: "#E74C3C",
  dropped: "#E74C3C",
  spammed: "#E74C3C",
  deffered: "#E74C3C",
};

const EVENT_ICON_MAP = {
  processed: (color) => <CheckOutlinedIcon style={{ fontSize: 18, color }} />,
  delivered: (color) => <DoneAllOutlinedIcon style={{ fontSize: 18, color }} />,
  open: (color) => <DraftsOutlinedIcon style={{ fontSize: 18, color }} />,
  click: (color) => <AdsClickOutlinedIcon style={{ fontSize: 18, color }} />,
  bounce: (color) => <CancelOutlinedIcon style={{ fontSize: 18, color }} />,
  dropped: (color) => (
    <MarkunreadMailboxOutlinedIcon style={{ fontSize: 18, color }} />
  ),
  spammed: (color) => (
    <PrivacyTipOutlinedIcon style={{ fontSize: 18, color }} />
  ),
  deffered: (color) => (
    <ScheduleSendOutlinedIcon style={{ fontSize: 18, color }} />
  ),
};

const HOTEL_LOG_TYPES = ["BOOKING_HOTEL", "CHECKIN_HOTEL", "BEFORE_CHECKOUT_HOTEL", "AFTER_CHECKOUT_HOTEL"];
const TRAVELLER_LOG_TYPES = ["TO_TRAVELLER_PRIOR_CHECKIN", "TO_TRAVELLER_ON_CHECKIN_DAY", "TO_TRAVELLER_BEFORE_CHECKOUT",];

const parseLogs = (emailLogs) => {
  if (!emailLogs || !Array.isArray(emailLogs)) return [];
  return emailLogs
    .map((log) => {
      if (typeof log === "string") {
        try { return JSON.parse(log); } catch { return null; }
      }
      return log;
    })
    .filter((log) => log && typeof log === "object")
    .map((log) => ({
      ...log,
      log_type: log.log_type || log.logType,
    }));
};

const getEarliestSendDate = (logs) => {
  const allTimestamps = logs.flatMap((log) =>
    (log.events || []).map((e) => e.timestamp).filter(Boolean)
  );
  if (allTimestamps.length === 0) return null;
  return Math.min(...allTimestamps);
};

// Returns the "First Mail" date/time for a single log type (BOOKING_HOTEL, CHECKIN_HOTEL, etc.)
// Used to populate one export column per log type instead of one combined column.
// Falls back to the COMMON_TEMPLATE send date for hotel log types, matching the
// grid (traveller mails don't count the common template as sent).
export const getFirstMailForLogType = (emailLogs, logType) => {
  const parsed = parseLogs(emailLogs);
  const earliest = getEarliestSendDate(parsed.filter((log) => log.log_type === logType));
  if (earliest) return moment.unix(earliest).format("DD/MM/YYYY");

  if (logType.startsWith("TO_TRAVELLER_")) return "";
  const commonEarliest = getEarliestSendDate(parsed.filter((log) => log.log_type === "COMMON_TEMPLATE"));
  return commonEarliest ? `${moment.unix(commonEarliest).format("DD/MM/YYYY")} (Common Email)` : "";
};


// Reuses parseBookingDate (see top of file) so this tooltip parses the same
// booking/checkin/checkout values the same way the grid's own date columns
// do — those handle far more real-world formats than a strict moment list.
const parseFlexibleMoment = (value) => {
  const date = parseBookingDate(value);
  return date ? moment(date) : null;
};

const renderSegmentedLogSummary = (emailLogs, bookingDateStr, logTypes, createdAt, checkinDateStr, checkoutDateStr) => {
  const parsed = parseLogs(emailLogs);
  const filtered = parsed.filter((log) => logTypes.includes(log.log_type));
  const commonTemplateLog = parsed.find((log) => log.log_type === "COMMON_TEMPLATE");

  const today = moment().startOf("day");
  const isFuture = (m) => m && m.isAfter(today, "day");

  const earliest = getEarliestSendDate(filtered.filter((l) => l.log_type === "BOOKING_HOTEL"));
  const sentMoment = earliest ? moment.unix(earliest) : null;
  const sentDate = sentMoment ? sentMoment.format("DD-MM-YYYY HH:mm") : null;

  const createdMoment = createdAt
    ? moment(Number(createdAt)).isValid() ? moment(Number(createdAt)) : moment(createdAt)
    : null;
  const bookingMoment = parseFlexibleMoment(bookingDateStr);
  const checkinMoment = parseFlexibleMoment(checkinDateStr);
  const checkoutMoment = parseFlexibleMoment(checkoutDateStr);

  const createdAtFormatted = createdMoment?.format("DD-MM-YYYY") || null;
  const bookingDateFormatted = bookingMoment?.format("DD-MM-YYYY") || null;
  const checkinDateFormatted = checkinMoment?.format("DD-MM-YYYY") || null;
  const checkoutDateFormatted = checkoutMoment?.format("DD-MM-YYYY") || null;

  // 24-hour grace: each deadline is extended by 24h so emails sent the next day still count as on time.
  // Reference dates (booking/checkin/checkout) have no time component, so we use endOf("day") + 24h
  // for an exact 24h window beyond the expected send date.

  // --- BOOKING_HOTEL badge logic ---
  const isCreatedSameAsBooking = createdMoment && bookingMoment ? createdMoment.isSame(bookingMoment, "day") : false;
  const isSentSameAsCreated = sentMoment && createdMoment ? sentMoment.isSame(createdMoment, "day") : false;
  // On time if sent within booking date + 24h grace
  const bookingDeadline = bookingMoment ? bookingMoment.clone().endOf("day").add(24, "hours") : null;
  const isSentWithinBookingDeadline = sentMoment && bookingDeadline ? sentMoment.isSameOrBefore(bookingDeadline) : false;

  let badgeColor = "#faad14";
  let badgeLabel = "~ delayed";
  if (isCreatedSameAsBooking && isSentWithinBookingDeadline) {
    badgeColor = "#3D9140"; badgeLabel = "✓ on time";
  } else if (!isCreatedSameAsBooking && isSentSameAsCreated) {
    badgeColor = "#faad14"; badgeLabel = "~ late creation";
  } else if (isCreatedSameAsBooking && !isSentWithinBookingDeadline) {
    badgeColor = "#faad14"; badgeLabel = "~ sent late";
  }

  // --- CHECKIN_HOTEL badge logic ---
  // Deadline = checkin day at 12:00; with 24h grace → checkin+1 day at 12:00
  const checkinEarliest = getEarliestSendDate(filtered.filter((l) => l.log_type === "CHECKIN_HOTEL"));
  const checkinSentMoment = checkinEarliest ? moment.unix(checkinEarliest) : null;
  const checkinSentDate = checkinSentMoment ? checkinSentMoment.format("DD-MM-YYYY HH:mm") : null;
  const checkinDeadline = checkinMoment
    ? checkinMoment.clone().add(1, "day").set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
    : null;
  const isCheckinOnTime = checkinSentMoment && checkinDeadline ? checkinSentMoment.isSameOrBefore(checkinDeadline) : false;

  let checkinBadgeColor = "#faad14";
  let checkinBadgeLabel = "~ delayed";
  if (isCheckinOnTime) { checkinBadgeColor = "#3D9140"; checkinBadgeLabel = "✓ on time"; }

  // --- BEFORE_CHECKOUT_HOTEL badge logic ---
  // Deadline = checkout date; with 24h grace → checkout+1 day end
  const beforeCheckoutEarliest = getEarliestSendDate(filtered.filter((l) => l.log_type === "BEFORE_CHECKOUT_HOTEL"));
  const beforeCheckoutSentMoment = beforeCheckoutEarliest ? moment.unix(beforeCheckoutEarliest) : null;
  const beforeCheckoutSentDate = beforeCheckoutSentMoment ? beforeCheckoutSentMoment.format("DD-MM-YYYY HH:mm") : null;
  const beforeCheckoutDeadline = checkoutMoment ? checkoutMoment.clone().endOf("day").add(24, "hours") : null;
  const isBeforeCheckoutSentOnExpected = beforeCheckoutSentMoment && beforeCheckoutDeadline
    ? beforeCheckoutSentMoment.isSameOrBefore(beforeCheckoutDeadline) : false;

  let beforeCheckoutBadgeColor = "#ff4d4f";
  let beforeCheckoutBadgeLabel = "✗ not sent";
  if (beforeCheckoutSentMoment) {
    beforeCheckoutBadgeColor = isBeforeCheckoutSentOnExpected ? "#3D9140" : "#faad14";
    beforeCheckoutBadgeLabel = isBeforeCheckoutSentOnExpected ? "✓ on time" : "~ delayed";
  }

  // --- AFTER_CHECKOUT_HOTEL badge logic ---
  // Deadline = checkout+1 day; with 24h grace → checkout+2 days end
  const afterCheckoutEarliest = getEarliestSendDate(filtered.filter((l) => l.log_type === "AFTER_CHECKOUT_HOTEL"));
  const afterCheckoutSentMoment = afterCheckoutEarliest ? moment.unix(afterCheckoutEarliest) : null;
  const afterCheckoutSentDate = afterCheckoutSentMoment ? afterCheckoutSentMoment.format("DD-MM-YYYY HH:mm") : null;
  const afterCheckoutDeadline = checkoutMoment ? checkoutMoment.clone().add(1, "day").endOf("day").add(24, "hours") : null;
  const isAfterCheckoutOnTime = afterCheckoutSentMoment && afterCheckoutDeadline
    ? afterCheckoutSentMoment.isSameOrBefore(afterCheckoutDeadline) : false;

  let afterCheckoutBadgeColor = "#ff4d4f";
  let afterCheckoutBadgeLabel = "✗ not sent";
  if (afterCheckoutSentMoment) {
    afterCheckoutBadgeColor = isAfterCheckoutOnTime ? "#3D9140" : "#faad14";
    afterCheckoutBadgeLabel = isAfterCheckoutOnTime ? "✓ on time" : "~ delayed";
  }

  // --- TO_TRAVELLER_PRIOR_CHECKIN badge logic ---
  // Deadline = checkin date; with 24h grace → checkin+1 day end
  const priorCheckinEarliest = getEarliestSendDate(filtered.filter((l) => l.log_type === "TO_TRAVELLER_PRIOR_CHECKIN"));
  const priorCheckinSentMoment = priorCheckinEarliest ? moment.unix(priorCheckinEarliest) : null;
  const priorCheckinSentDate = priorCheckinSentMoment ? priorCheckinSentMoment.format("DD-MM-YYYY HH:mm") : null;
  const priorCheckinDeadline = checkinMoment ? checkinMoment.clone().endOf("day").add(24, "hours") : null;
  const isPriorCheckinOnTime = priorCheckinSentMoment && priorCheckinDeadline
    ? priorCheckinSentMoment.isSameOrBefore(priorCheckinDeadline) : false;

  let priorCheckinBadgeColor = "#ff4d4f";
  let priorCheckinBadgeLabel = "✗ not sent";
  if (priorCheckinSentMoment) {
    priorCheckinBadgeColor = isPriorCheckinOnTime ? "#3D9140" : "#faad14";
    priorCheckinBadgeLabel = isPriorCheckinOnTime ? "✓ on time" : "~ delayed";
  }

  // --- TO_TRAVELLER_ON_CHECKIN_DAY badge logic ---
  // Deadline = checkin date end; with 24h grace → checkin+1 day end
  const onCheckinDayEarliest = getEarliestSendDate(filtered.filter((l) => l.log_type === "TO_TRAVELLER_ON_CHECKIN_DAY"));
  const onCheckinDaySentMoment = onCheckinDayEarliest ? moment.unix(onCheckinDayEarliest) : null;
  const onCheckinDaySentDate = onCheckinDaySentMoment ? onCheckinDaySentMoment.format("DD-MM-YYYY HH:mm") : null;
  const onCheckinDayDeadline = checkinMoment ? checkinMoment.clone().endOf("day").add(24, "hours") : null;
  const isOnCheckinDayOnTime = onCheckinDaySentMoment && onCheckinDayDeadline
    ? onCheckinDaySentMoment.isSameOrBefore(onCheckinDayDeadline) : false;

  let onCheckinDayBadgeColor = "#ff4d4f";
  let onCheckinDayBadgeLabel = "✗ not sent";
  if (onCheckinDaySentMoment) {
    onCheckinDayBadgeColor = isOnCheckinDayOnTime ? "#3D9140" : "#faad14";
    onCheckinDayBadgeLabel = isOnCheckinDayOnTime ? "✓ on time" : "~ delayed";
  }

  // --- TO_TRAVELLER_BEFORE_CHECKOUT badge logic ---
  // Deadline = checkout date; with 24h grace → checkout+1 day end
  const travBeforeCheckoutEarliest = getEarliestSendDate(filtered.filter((l) => l.log_type === "TO_TRAVELLER_BEFORE_CHECKOUT"));
  const travBeforeCheckoutSentMoment = travBeforeCheckoutEarliest ? moment.unix(travBeforeCheckoutEarliest) : null;
  const travBeforeCheckoutSentDate = travBeforeCheckoutSentMoment ? travBeforeCheckoutSentMoment.format("DD-MM-YYYY HH:mm") : null;
  const travBeforeCheckoutDeadline = checkoutMoment ? checkoutMoment.clone().endOf("day").add(24, "hours") : null;
  const isTravBeforeCheckoutOnTime = travBeforeCheckoutSentMoment && travBeforeCheckoutDeadline
    ? travBeforeCheckoutSentMoment.isSameOrBefore(travBeforeCheckoutDeadline) : false;

  let travBeforeCheckoutBadgeColor = "#ff4d4f";
  let travBeforeCheckoutBadgeLabel = "✗ not sent";
  if (travBeforeCheckoutSentMoment) {
    travBeforeCheckoutBadgeColor = isTravBeforeCheckoutOnTime ? "#3D9140" : "#faad14";
    travBeforeCheckoutBadgeLabel = isTravBeforeCheckoutOnTime ? "✓ on time" : "~ delayed";
  }

  const isHotelColumn = logTypes.includes("BOOKING_HOTEL");
  const logMap = {};
  filtered.forEach((log) => { logMap[log.log_type] = log; });

  // --- Upcoming dot color and label ---
  const UPCOMING_COLOR = "#2D3748";
  const UPCOMING_LABEL = "⏳ upcoming";
  const UPCOMING_TEXT = "Email yet to be sent";

  // --- Helper: build missing/upcoming tooltip content ---
  const buildMissingTooltip = (title, rows, isUpcoming) => (
    <div style={{ maxWidth: 320, display: "flex", flexDirection: "column" }}>
      <div style={{ paddingBottom: 8, borderBottom: "1px solid #333", marginBottom: 8 }}>
        <div style={{ color: "white", fontWeight: 600, fontSize: 12, marginBottom: 8 }}>{title}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {rows.map(({ label, value, minWidth }, i) => value && (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "#aaa", fontSize: 10, minWidth: minWidth || 80 }}>{label}</span>
              <span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{value}</span>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {isUpcoming
              ? <span style={{ color: "#fff", fontSize: 10 }}>{UPCOMING_TEXT}</span>
              : <span style={{ color: "#ff4d4f", fontSize: 10 }}>No email sent</span>
            }
          </div>
        </div>
      </div>
    </div>
  );

  // --- Per log type: relevant date moment for future check ---
  const logTypeDateMap = {
    BOOKING_HOTEL: bookingMoment,
    CHECKIN_HOTEL: checkinMoment,
    BEFORE_CHECKOUT_HOTEL: checkoutMoment,
    AFTER_CHECKOUT_HOTEL: checkoutMoment ? checkoutMoment.clone().add(1, "day") : null,
    TO_TRAVELLER_PRIOR_CHECKIN: checkinMoment,
    TO_TRAVELLER_ON_CHECKIN_DAY: checkinMoment,
    TO_TRAVELLER_BEFORE_CHECKOUT: checkoutMoment,
  };

  // --- Per log type: tooltip rows config ---
  const logTypeTooltipRows = {
    BOOKING_HOTEL: [
      { label: "Created At:", value: createdAtFormatted },
      { label: "Booking Date:", value: bookingDateFormatted },
      { label: "Checkin Date:", value: checkinDateFormatted },
    ],
    CHECKIN_HOTEL: [
      { label: "Created At:", value: createdAtFormatted },
      { label: "Checkin Date:", value: checkinDateFormatted },
    ],
    BEFORE_CHECKOUT_HOTEL: [
      { label: "Created At:", value: createdAtFormatted, minWidth: 110 },
      { label: "Checkout Date:", value: checkoutDateFormatted, minWidth: 110 },
    ],
    AFTER_CHECKOUT_HOTEL: [
      { label: "Created At:", value: createdAtFormatted, minWidth: 110 },
      { label: "Checkout Date:", value: checkoutDateFormatted, minWidth: 110 },
    ],
    TO_TRAVELLER_PRIOR_CHECKIN: [
      { label: "Created At:", value: createdAtFormatted, minWidth: 90 },
      { label: "Checkin Date:", value: checkinDateFormatted, minWidth: 90 },
    ],
    TO_TRAVELLER_ON_CHECKIN_DAY: [
      { label: "Created At:", value: createdAtFormatted, minWidth: 90 },
      { label: "Checkin Date:", value: checkinDateFormatted, minWidth: 90 },
    ],
    TO_TRAVELLER_BEFORE_CHECKOUT: [
      { label: "Created At:", value: createdAtFormatted, minWidth: 110 },
      { label: "Checkout Date:", value: checkoutDateFormatted, minWidth: 110 },
    ],
  };

  // --- Helper: red or blue dot ---
  const statusDot = (key, positionNumber, logType, tooltip, isUpcoming) => (
    <Tooltip
      key={key}
      placement="bottom"
      overlayInnerStyle={{ backgroundColor: "#1C1C1C", borderRadius: 8, padding: 10 }}
      title={tooltip}
    >
      <div style={{
        backgroundColor: isUpcoming ? UPCOMING_COLOR : "#ff4d4f",
        color: "white",
        cursor: "pointer",
        borderRadius: "50%",
        minWidth: 20,
        height: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: "bold",
        flexShrink: 0,
      }}>
        {positionNumber}
      </div>
    </Tooltip>
  );

  // --- Present log dot color map ---
  const presentDotColorMap = {
    BOOKING_HOTEL: badgeColor,
    CHECKIN_HOTEL: checkinBadgeColor,
    BEFORE_CHECKOUT_HOTEL: beforeCheckoutBadgeColor,
    AFTER_CHECKOUT_HOTEL: afterCheckoutBadgeColor,
    TO_TRAVELLER_PRIOR_CHECKIN: priorCheckinBadgeColor,
    TO_TRAVELLER_ON_CHECKIN_DAY: onCheckinDayBadgeColor,
    TO_TRAVELLER_BEFORE_CHECKOUT: travBeforeCheckoutBadgeColor,
  };

  // --- Present log tooltip content map ---
  const presentTooltipContentMap = {
    BOOKING_HOTEL: (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {createdAtFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 80 }}>Created At:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{createdAtFormatted}</span></div>}
        {bookingDateFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 80 }}>Booking Date:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{bookingDateFormatted}</span></div>}
        {checkinDateFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 80 }}>Checkin Date:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{checkinDateFormatted}</span></div>}
        {sentDate && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 80 }}>First Mail:</span><span style={{ background: badgeColor, color: "#fff", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>{sentDate}</span><span style={{ color: badgeColor, fontSize: 10 }}>{badgeLabel}</span></div>}
      </div>
    ),
    CHECKIN_HOTEL: (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {createdAtFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 80 }}>Created At:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{createdAtFormatted}</span></div>}
        {checkinDateFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 80 }}>Checkin Date:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{checkinDateFormatted}</span></div>}
        {checkinSentDate && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 80 }}>First Mail:</span><span style={{ background: checkinBadgeColor, color: "#fff", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>{checkinSentDate}</span><span style={{ color: checkinBadgeColor, fontSize: 10 }}>{checkinBadgeLabel}</span></div>}
      </div>
    ),
    BEFORE_CHECKOUT_HOTEL: (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {createdAtFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 110 }}>Created At:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{createdAtFormatted}</span></div>}
        {checkoutDateFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 110 }}>Checkout Date:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{checkoutDateFormatted}</span></div>}
        {beforeCheckoutSentDate && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 110 }}>First Mail:</span><span style={{ background: beforeCheckoutBadgeColor, color: "#fff", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>{beforeCheckoutSentDate}</span><span style={{ color: beforeCheckoutBadgeColor, fontSize: 10 }}>{beforeCheckoutBadgeLabel}</span></div>}
      </div>
    ),
    AFTER_CHECKOUT_HOTEL: (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {createdAtFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 110 }}>Created At:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{createdAtFormatted}</span></div>}
        {checkoutDateFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 110 }}>Checkout Date:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{checkoutDateFormatted}</span></div>}
        {afterCheckoutSentDate && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 110 }}>First Mail:</span><span style={{ background: afterCheckoutBadgeColor, color: "#fff", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>{afterCheckoutSentDate}</span><span style={{ color: afterCheckoutBadgeColor, fontSize: 10 }}>{afterCheckoutBadgeLabel}</span></div>}
      </div>
    ),
    TO_TRAVELLER_PRIOR_CHECKIN: (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {createdAtFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 90 }}>Created At:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{createdAtFormatted}</span></div>}
        {checkinDateFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 90 }}>Checkin Date:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{checkinDateFormatted}</span></div>}
        {priorCheckinSentDate && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 90 }}>First Mail:</span><span style={{ background: priorCheckinBadgeColor, color: "#fff", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>{priorCheckinSentDate}</span><span style={{ color: priorCheckinBadgeColor, fontSize: 10 }}>{priorCheckinBadgeLabel}</span></div>}
      </div>
    ),
    TO_TRAVELLER_ON_CHECKIN_DAY: (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {createdAtFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 90 }}>Created At:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{createdAtFormatted}</span></div>}
        {checkinDateFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 90 }}>Checkin Date:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{checkinDateFormatted}</span></div>}
        {onCheckinDaySentDate && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 90 }}>First Mail:</span><span style={{ background: onCheckinDayBadgeColor, color: "#fff", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>{onCheckinDaySentDate}</span><span style={{ color: onCheckinDayBadgeColor, fontSize: 10 }}>{onCheckinDayBadgeLabel}</span></div>}
      </div>
    ),
    TO_TRAVELLER_BEFORE_CHECKOUT: (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {createdAtFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 110 }}>Created At:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{createdAtFormatted}</span></div>}
        {checkoutDateFormatted && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 110 }}>Checkout Date:</span><span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{checkoutDateFormatted}</span></div>}
        {travBeforeCheckoutSentDate && <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#aaa", fontSize: 10, minWidth: 110 }}>First Mail:</span><span style={{ background: travBeforeCheckoutBadgeColor, color: "#fff", borderRadius: 4, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>{travBeforeCheckoutSentDate}</span><span style={{ color: travBeforeCheckoutBadgeColor, fontSize: 10 }}>{travBeforeCheckoutBadgeLabel}</span></div>}
      </div>
    ),
  };


  const dots = logTypes.map((logType, index) => {
    const log = logMap[logType];
    const positionNumber = index + 1;
    const noEvents = log && (log.events || []).length === 0;
    const relevantDateMoment = logTypeDateMap[logType];
    const isUpcoming = isFuture(relevantDateMoment);

    // --- No log at all ---
    if (!log) {
      if (logType === "BOOKING_HOTEL" && !isHotelColumn) return null;

      // If a COMMON_TEMPLATE log exists, render it as a yellow dot for this slot
      // Passenger mail types (TO_TRAVELLER_*) remain red even when common template exists
      const isPassengerMail = logType.startsWith("TO_TRAVELLER_");
      if (commonTemplateLog && !isUpcoming && !isPassengerMail) {
        return (
          <Tooltip
            key={logType}
            placement="bottom"
            overlayInnerStyle={{ backgroundColor: "#1C1C1C", borderRadius: 8, padding: 10 }}
            title={
              <div style={{ maxWidth: 320, display: "flex", flexDirection: "column" }}>
                <div style={{ paddingBottom: 8, borderBottom: "1px solid #333", marginBottom: 8, flexShrink: 0 }}>
                  <div style={{ color: "white", fontWeight: 600, fontSize: 12, marginBottom: 8 }}>{logType}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {(logTypeTooltipRows[logType] || []).map(({ label, value, minWidth }, i) => value && (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ color: "#aaa", fontSize: 10, minWidth: minWidth || 80 }}>{label}</span>
                        <span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{value}</span>
                      </div>
                    ))}
                    <span style={{ color: "#faad14", fontSize: 10 }}>Via Common Template</span>
                  </div>
                </div>
                <div style={{ maxHeight: 180, overflowY: "auto" }}>
                  {(commonTemplateLog.events || []).length > 0 ? (
                    [...commonTemplateLog.events]
                      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
                      .map((event, i) => {
                        const eventColor = colorOfEmailEvent[event.event] || "#A0A0A0";
                        const EventIcon = EVENT_ICON_MAP[event.event];
                        return (
                          <Tag key={i} style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4, whiteSpace: "normal", wordBreak: "break-word", marginBottom: 4 }}>
                            {EventIcon ? EventIcon(eventColor) : null}
                            <div style={{ display: "flex", flexDirection: "column" }}>
                              <span style={{ color: "#000", fontWeight: 500 }}>{event.email || "No Email"}</span>
                              <span>
                                <strong style={{ color: event.event === "bounce" ? "red" : "#000" }}>{event.event}</strong>{" "}
                                at {formatDate(event.timestamp)}
                              </span>
                            </div>
                          </Tag>
                        );
                      })
                  ) : (
                    <div style={{ color: "#999", fontSize: 11 }}>No events</div>
                  )}
                </div>
              </div>
            }
          >
            <div style={{
              backgroundColor: "#faad14",
              color: "white",
              cursor: "pointer",
              borderRadius: "50%",
              minWidth: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: "bold",
              flexShrink: 0,
            }}>
              {positionNumber}
            </div>
          </Tooltip>
        );
      }

      const tooltip = buildMissingTooltip(logType, logTypeTooltipRows[logType] || [], isUpcoming);
      return statusDot(logType, positionNumber, logType, tooltip, isUpcoming);
    }

    // --- Log exists but events not yet received (mail sent, awaiting tracking) ---
    if (noEvents) {
      return (
        <Tooltip
          key={logType}
          placement="bottom"
          overlayInnerStyle={{ backgroundColor: "#1C1C1C", borderRadius: 8, padding: 10 }}
          title={
            <div style={{ maxWidth: 320, display: "flex", flexDirection: "column" }}>
              <div style={{ paddingBottom: 8, borderBottom: "1px solid #333", marginBottom: 8 }}>
                <div style={{ color: "white", fontWeight: 600, fontSize: 12, marginBottom: 8 }}>{logType}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {(logTypeTooltipRows[logType] || []).map(({ label, value, minWidth }, i) => value && (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ color: "#aaa", fontSize: 10, minWidth: minWidth || 80 }}>{label}</span>
                      <span style={{ color: "#fff", fontSize: 10, fontWeight: 600 }}>{value}</span>
                    </div>
                  ))}
                  <span style={{ color: "#faad14", fontSize: 10 }}>Mail sent, awaiting tracking events</span>
                </div>
              </div>
            </div>
          }
        >
          <div style={{
            backgroundColor: "#faad14",
            color: "white",
            cursor: "pointer",
            borderRadius: "50%",
            minWidth: 20,
            height: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: "bold",
            flexShrink: 0,
          }}>
            {positionNumber}
          </div>
        </Tooltip>
      );
    }

    // --- Log present with events — show full tooltip ---
    const dotColor = presentDotColorMap[logType] || (log.log_type?.toLowerCase().includes("manual") ? "#fa8c16" : "#3d9140");
    const tooltipContent = presentTooltipContentMap[logType] || null;

    return (
      <Tooltip
        key={logType}
        placement="bottom"
        overlayInnerStyle={{ backgroundColor: "#1C1C1C", borderRadius: 8, padding: 10 }}
        title={
          <div style={{ maxWidth: 320, display: "flex", flexDirection: "column" }}>
            <div style={{ paddingBottom: 8, borderBottom: "1px solid #333", marginBottom: 8, flexShrink: 0 }}>
              <div style={{ color: "white", fontWeight: 600, fontSize: 12, marginBottom: 8 }}>{log.log_type}</div>
              {tooltipContent}
            </div>
            <div style={{ maxHeight: 180, overflowY: "auto" }}>
              {(log.events || []).length > 0 ? (
                [...log.events]
                  .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
                  .map((event, i) => {
                    const eventColor = colorOfEmailEvent[event.event] || "#A0A0A0";
                    const EventIcon = EVENT_ICON_MAP[event.event];
                    return (
                      <Tag key={i} style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 4, whiteSpace: "normal", wordBreak: "break-word", marginBottom: 4 }}>
                        {EventIcon ? EventIcon(eventColor) : null}
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ color: "#000", fontWeight: 500 }}>{event.email || "No Email"}</span>
                          <span>
                            <strong style={{ color: event.event === "bounce" ? "red" : "#000" }}>{event.event}</strong>{" "}
                            at {formatDate(event.timestamp)}
                          </span>
                        </div>
                      </Tag>
                    );
                  })
              ) : (
                <div style={{ color: "#999", fontSize: 11 }}>No events</div>
              )}
            </div>
          </div>
        }
      >
        <div style={{
          backgroundColor: dotColor,
          color: "white",
          cursor: "pointer",
          borderRadius: "50%",
          minWidth: 20,
          height: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: "bold",
          flexShrink: 0,
        }}>
          {positionNumber}
        </div>
      </Tooltip>
    );
  });

  const hasAnyDot = dots.some((d) => d !== null);
  if (!hasAnyDot) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, height: "100%" }}>
      {dots}
    </div>
  );
};


const renderManualLogSummary = (emailLogs) => {
  const parsed = parseLogs(emailLogs);
  const manualLogs = parsed.filter((log) => log.log_type?.toLowerCase().includes("manual"));

  if (manualLogs.length === 0) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, height: "100%" }}>
      {manualLogs.map((log, index) => (
        <Tooltip
          key={index}
          placement="bottom"
          overlayInnerStyle={{ backgroundColor: "#1C1C1C", borderRadius: 8, padding: 10 }}
          title={
            <div style={{ maxWidth: 320, display: "flex", flexDirection: "column" }}>
              <div style={{ paddingBottom: 8, borderBottom: "1px solid #333", marginBottom: 8, flexShrink: 0 }}>
                <div style={{ color: "white", fontWeight: 600, fontSize: 12, marginBottom: 8 }}>
                  {log.log_type}
                </div>
                <div style={{ color: "#fa8c16", fontSize: 10 }}>Manual email log</div>
              </div>
              <div style={{ maxHeight: 180, overflowY: "auto" }}>
                {(log.events || []).length > 0 ? (
                  [...log.events]
                    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
                    .map((event, i) => {
                      const eventColor = colorOfEmailEvent[event.event] || "#A0A0A0";
                      const EventIcon = EVENT_ICON_MAP[event.event];
                      return (
                        <Tag
                          key={i}
                          style={{
                            fontSize: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            marginBottom: 4,
                          }}
                        >
                          {EventIcon ? EventIcon(eventColor) : null}
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ color: "#000", fontWeight: 500 }}>
                              {event.email || "No Email"}
                            </span>
                            <span>
                              <strong style={{ color: event.event === "bounce" ? "red" : "#000" }}>
                                {event.event}
                              </strong>{" "}
                              at {formatDate(event.timestamp)}
                            </span>
                          </div>
                        </Tag>
                      );
                    })
                ) : (
                  <div style={{ color: "#999", fontSize: 11 }}>No events</div>
                )}
              </div>
            </div>
          }
        >
          <div
            style={{
              backgroundColor: "#fa8c16",
              color: "white",
              cursor: "pointer",
              borderRadius: "50%",
              minWidth: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: "bold",
              flexShrink: 0,
            }}
          >
            {index + 1}
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

const handleRenderEmailEvents = (emailLogs) => {
  // Handle null, undefined, or empty array
  if (!emailLogs || !Array.isArray(emailLogs) || emailLogs.length === 0) {
    return null;
  }
  // Parse JSON strings if needed
  const parsedLogs = emailLogs
    .map((log) => {
      if (typeof log === "string") {
        try {
          return JSON.parse(log);
        } catch (e) {
          console.warn("Failed to parse email log JSON string:", e, log);
          return null;
        }
      }
      return log;
    })
    .filter((log) => log && typeof log === "object");
  if (parsedLogs.length === 0) {
    return null;
  }
  // :arrow_down_small: SORT EVENTS IN DESCENDING ORDER (LATEST FIRST)
  const sortedLogs = parsedLogs.map((log) => {
    if (!Array.isArray(log.events)) {
      return { ...log, events: [] };
    }
    return {
      ...log,
      events: [...log.events].sort(
        (a, b) => (b.timestamp || 0) - (a.timestamp || 0),
      ),
    };
  });
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {sortedLogs.map((log, index) => (
        <Tooltip
          key={index}
          placement="bottom"
          title={
            <div
              style={{
                maxHeight: 200,
                maxWidth: 350,
                overflowY: "auto",
                padding: 8,
                backgroundColor: "#1C1C1C",
                borderRadius: 8,
              }}
            >
              <Typography style={{ color: "white", marginBottom: 8 }}>
                <strong></strong> {log.log_type || "No Log Type"}
              </Typography>
              {log.events.length > 0 ? (
                log.events.map((event, i) => {
                  const eventType = event.event || "unknown";
                  const eventColor = colorOfEmailEvent[eventType] || "#A0A0A0";
                  const EventIcon = EVENT_ICON_MAP[eventType];
                  return (
                    <Tag
                      key={i}
                      style={{
                        fontSize: 10,
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        marginBottom: 4,
                      }}
                    >
                      {EventIcon ? EventIcon(eventColor) : null}
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ color: "#000", fontWeight: 500 }}>
                          :email: {event.email || "No Email"}
                        </span>
                        <span>
                          <strong
                            style={{
                              color: event.event === "bounce" ? "red" : "#000",
                            }}
                          >
                            {event.event}
                          </strong>{" "}
                          at {formatDate(event.timestamp)}
                        </span>
                      </div>
                    </Tag>
                  );
                })
              ) : (
                <div style={{ color: "#999", fontSize: 12 }}>
                  No events available
                </div>
              )}
            </div>
          }
        >
          <div
            style={{
              backgroundColor: log.log_type?.toLowerCase().includes("manual") ? "#fa8c16" : "#3d9140",
              color: "white",
              cursor: "pointer",
              borderRadius: "50%",
              minWidth: "20px",
              height: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "11px",
              fontWeight: "bold",
              flexShrink: 0,
            }}
          >
            {index + 1}
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

// const FollowUpStatusRenderer = ({ value }) => {
//   const isEmpty = !value;
//   const displayValue = isEmpty ? 'Select status' : value;

//   return (
//     <span style={{ color: isEmpty ? '#aaa' : 'inherit' }}>
//       {displayValue} <span style={{ color: '#999' }}>▼</span>
//     </span>
//   );
// }

const maskValue = (value) => {
  const str = Array.isArray(value) ? value.join(", ") : value;
  if (!str) return str;
  return `${String(str).slice(0, 2)}*******`;
};

export const HOTEL_FOLLOWUP_COLUMN_DEFS = (props) => {
  const shouldMaskCustomerData =
    props?.domain?.toLowerCase() === "bcd" &&
    props?.workspaceName?.toLowerCase() === "demo";

  return [
    {
      headerName: "Select",
      field: "id", // or any key
      checkboxSelection: (params) => {
        // Only show checkbox for child rows under a group
        return !params.node.group && params.node.parent != null;
      },
      headerCheckboxSelection: false,
      width: 100,
      pinned: "left",
    },
    {
      headerName: "GSTIN Match Status",
      field: "gstMatchStatus",
      columnGroupShow: "close",     // visible when the group is collapsed
      enableRowGroup: true,
      filter: "agSetColumnFilter",
      filterParams: { values: ["MATCH", "MISMATCH"] },
      cellRenderer: (params) => {
        if (params.node.group) return null;
        const value = params.value?.toUpperCase();
        if (value === "MATCH") {
          return (
            <Tag
              color="green"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                borderRadius: "5px", padding: "4px 10px", fontWeight: 500
              }}
            >
              <CheckCircleFilled style={{ fontSize: "14px" }} /> MATCH
            </Tag>
          );
        }
        if (value === "MISMATCH") {
          return (
            <Tag
              color="red"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                borderRadius: "5px", padding: "4px 10px", fontWeight: 500
              }}
            >
              <CloseCircleFilled style={{ fontSize: "14px" }} /> MISMATCH
            </Tag>
          );
        }
        return null;
      },
    },
    // {
    //   headerName: "GSTIN Match Details",
    //   children: [
    //     {
    //       headerName: "GSTIN Match Status",
    //       field: "gstMatchStatus",
    //       columnGroupShow: "close",     // visible when the group is collapsed
    //       enableRowGroup: true,
    //       filter: "agSetColumnFilter",
    //       filterParams: { values: ["MATCH", "MISMATCH"] },
    //       cellRenderer: (params) => {
    //         if (params.node.group) return null;
    //         const value = params.value?.toUpperCase();
    //         if (value === "MATCH") {
    //           return (
    //             <Tag
    //               color="green"
    //               style={{ display: "inline-flex", alignItems: "center", gap: 4,
    //                        borderRadius: "5px", padding: "4px 10px", fontWeight: 500 }}
    //             >
    //               <CheckCircleFilled style={{ fontSize: "14px" }} /> MATCH
    //             </Tag>
    //           );
    //         }
    //         if (value === "MISMATCH") {
    //           return (
    //             <Tag
    //               color="red"
    //               style={{ display: "inline-flex", alignItems: "center", gap: 4,
    //                        borderRadius: "5px", padding: "4px 10px", fontWeight: 500 }}
    //             >
    //               <CloseCircleFilled style={{ fontSize: "14px" }} /> MISMATCH
    //             </Tag>
    //           );
    //         }
    //         return null;
    //       },
    //     },
    //     {
    //       headerName: "GSTIN Match Logic",
    //       colId: "gstMatchLogic",
    //       columnGroupShow: "open",
    //       filter: false,
    //       sortable: false,
    //       width: 260,
    //       valueGetter: (params) => {
    //         if (!params.data) return null;
    //         const hotelStateRaw = params.data.email_hotel_state;
    //         const hotelStateStr =
    //           Array.isArray(hotelStateRaw)
    //             ? String(hotelStateRaw[0] ?? "")
    //             : typeof hotelStateRaw === "string"
    //               ? hotelStateRaw
    //               : String(hotelStateRaw ?? "");
    //         const hotelCode = getGstCodeFromAbbr(hotelStateStr.trim());
    //         const gstinRaw = params.data.gstin;
    //         const clientGstinStr =
    //           Array.isArray(gstinRaw)
    //             ? String(gstinRaw[0] ?? "")
    //             : typeof gstinRaw === "string"
    //               ? gstinRaw
    //               : String(gstinRaw ?? "");
    //         const clientCode = getStateCodeFromGstin(clientGstinStr.trim());

    //         const bothResolved = !!(hotelCode && clientCode);
    //         const isMatch = bothResolved && hotelCode === clientCode;

    //         return { bothResolved, isMatch };
    //       },

    //       cellRenderer: (params) => {
    //         if (params.node.group || !params.value) return null;

    //         const { bothResolved, isMatch } = params.value;

    //         if (!bothResolved) {
    //           return (
    //             <span style={{ color: "#9e9e9e", fontSize: 12 }}>
    //               Insufficient data to compare
    //             </span>
    //           );
    //         }

    //         return isMatch ? (
    //           <span style={{ color: "#2e7d32", fontWeight: 500, fontSize: 12 }}>
    //             Hotel state = Client GSTIN state
    //           </span>
    //         ) : (
    //           <span style={{ color: "#d32f2f", fontWeight: 500, fontSize: 12 }}>
    //             Hotel state ≠ Client GSTIN state
    //           </span>
    //         );
    //       },
    //     },
    //     {
    //       headerName: "Hotel State",
    //       field: "email_hotel_state",
    //       colId: "gstMatchDetails_hotelState",
    //       columnGroupShow: "open",
    //       filter: "agTextColumnFilter",
    //       enableRowGroup: true,
    //     },
    //     {
    //       headerName: "Client GSTIN",
    //       field: "gstin",
    //       colId: "gstMatchDetails_clientGstin",
    //       columnGroupShow: "open",
    //       filter: "agTextColumnFilter",
    //       enableRowGroup: true,
    //       cellRenderer: (params) => {
    //         if (params.node.group) return params.value;
    //         const value = params.value;
    //         const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
    //         return (
    //           <div style={{ height: "100%", display: "flex", alignItems: "center" }}>
    //             {hasValue ? (
    //               <Tag color="blue" style={{ borderRadius: "5px", padding: "4px 12px", fontSize: "14px", fontWeight: 500 }}>
    //                 {Array.isArray(value) ? value[0] : value}
    //               </Tag>
    //             ) : (
    //               <Tag color="red" style={{ borderRadius: "5px", padding: "4px 12px", fontSize: "14px", fontWeight: 500 }}>
    //                 Entity Not Present in same State as hotel
    //               </Tag>
    //             )}
    //           </div>
    //         );
    //       },
    //     },      
    //   ],
    // },

    {
      headerName: "Customer Code",
      field: "customer_code",
      width: 180,
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      hide: props.serviceType === "INVOICE_FOLLOWUP",
      columnGroupShow: "open",
      cellRenderer: (params) => {
        if (params.node.group) return params.value;

        const value = params.value; // already a string from valueGetter
        const hasValue = value !== null && value !== undefined && value !== "";

        return (
          <div style={{ height: "100%", display: "flex", alignItems: "center" }}>
            {hasValue ? (
              <Tag
                color="purple"
                style={{
                  borderRadius: "5px",
                  padding: "4px 12px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {value}
              </Tag>
            ) : (
              <span style={{ color: "#999" }}>-</span>
            )}
          </div>
        );
      },
    },
    // {
    //   headerName: "Customer Code",
    //   field: "customer_code",
    //   width: 180,
    //   filter: "agTextColumnFilter",
    //   enableRowGroup: true,
    //   columnGroupShow: "open",
    //   valueGetter: (params) => {
    //     if (params.node.group) return params.node.key;
    //     const raw = params.data?.customer_code;
    //     if (raw === null || raw === undefined) return "";
    //     return String(raw);
    //   },
    //   cellRenderer: (params) => {
    //     if (params.node.group) return params.value;
    //     const value = params.value;
    //     if (!value) return <span style={{ color: "#999" }}>-</span>;
    //     return (
    //       <div style={{ height: "100%", display: "flex", alignItems: "center" }}>
    //         <span style={{ fontWeight: 500, color: "#531dab" }}>{value}</span>
    //       </div>
    //     );
    //   },
    // },
    {
      field: "booking_client_id",
      headerName: "booking_client_id",
      filter: "agSetColumnFilter",
      filterParams: {
        values: (params) => {
          const map = props.clientNamesMapRef?.current || {};
          params.success(Object.keys(map));
        },
      },
    },
    {
      field: "totalGST",
      headerName: "Total GST",
      filter: "agNumberColumnFilter",
    },
  

    {
      headerName: "Hotel Details",
      children: [
        {
          headerName: "Hotel Contact",
          field: "hotelContact",
          width: 180,
          sortable: false,
          filter: false,

          cellRenderer: (params) => {
            if (params.node.group) return "";
            const emails = params.data?.email_hotel_contact_email;
            const gstin = params.data?.email_hotel_gstin;

            const hasEmail =
              Array.isArray(emails) &&
              emails.some((email) => email && String(email).trim() !== "");

            const hasGstin =
              gstin !== null &&
              gstin !== undefined &&
              String(gstin).trim() !== "" &&
              String(gstin).trim().toLowerCase() !== "null" &&
              String(gstin).trim() !== "-";

            return (
              <div
                onClick={() => props.openEmailModal(params.data)}
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  cursor: "pointer",
                  padding: "0 8px",
                  fontWeight: 700,
                }}
              >
                <MailOutlined
                  style={{
                    fontSize: 18,
                    color: hasEmail ? "#2e7d32" : "#d32f2f",
                  }}
                />

                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: hasGstin ? "#2e7d32" : "#d32f2f",
                    letterSpacing: "0.3px",
                  }}
                >
                  GSTIN
                </span>

                <EditFilled
                  style={{
                    fontSize: 14,
                    color: "#1d3557",
                  }}
                />
              </div>
            );
          },
        },
        {
          headerName: "Hotel Name",
          field: "email_hotel_name",
          filter: "agTextColumnFilter",
          enableRowGroup: true,
          // checkboxSelection: true,
          //editable: true,
          columnGroupShow: "close",
          width: 280,
          //formatType:"ENTITY_WITH_LOGO"
        },
        {
          headerName: "Hotel GSTIN",
          field: "email_hotel_gstin",
          //editable: true,
          filter: "agTextColumnFilter",
          enableRowGroup: true,
          columnGroupShow: "open",
          formatType: "CHIP",
        },
        {
          headerName: "City",
          field: "email_hotel_city",
          filter: "agTextColumnFilter",
          enableRowGroup: true,
          //editable: true,
          columnGroupShow: "open",
        },
        {
          headerName: "Hotel Address",
          field: "email_hotel_address",
          filter: "agTextColumnFilter",
          enableRowGroup: true,
          //editable: true,
          columnGroupShow: "open",
          width: 350,
        },
        {
          headerName: "State",
          field: "email_hotel_state",
          enableRowGroup: true,
          filter: "agTextColumnFilter",
          columnGroupShow: "open",
          //formatType:"GSTIN_STATE"
        },
        {
          headerName: "Invalid Hotel Contact Email",
          field: "email_hotel_contact_invalid_email",
          enableRowGroup: true,
          width: 300,
          filter: "agTextColumnFilter",
          columnGroupShow: "open",
        },
        {
          headerName: "Hotel Contact Email",
          field: "email_hotel_contact_email",
          enableRowGroup: true,
          width: 300,
          columnGroupShow: "open",
          filter: "agTextColumnFilter",
          filterParams: {
            // Only allow 'equals' filtering for simplicity
            filterOptions: ["equals", "lessThan", "greaterThan"],
          },
          getQuickFilterText: (params) => {
            // Optional: for quick filter to work with length
            return params.value?.length?.toString() || "0";
          },
          cellRenderer: (params) => {
            if (params.node.group) {
              return null;
            }
            const emails = params.value; // Assuming params.value is an array of emails
            const visibleEmail = emails?.[0]; // Show only the first email
            const remainingCount = emails?.length > 1 ? emails.length - 1 : 0; // Count remaining emails

            return (
              <div
                style={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  flexWrap: "wrap",
                }}
              >
                {emails && Array.isArray(emails) && emails.length > 0 ? (
                  <>
                    <Tag icon={<MailOutlined />}>{visibleEmail}</Tag>

                    {remainingCount > 0 && (
                      <Tooltip
                        title={emails.slice(1).map((email, idx) => (
                          <div key={idx}>{email}</div>
                        ))}
                      >
                        <span style={{ color: "#2D3748", cursor: "pointer" }}>
                          +{remainingCount}
                        </span>
                      </Tooltip>
                    )}

                    <EditFilled
                      style={{ cursor: "pointer", marginLeft: "5px" }}
                      onClick={() => props.openEmailModal(params.data)}
                    />
                  </>
                ) : (
                  <Tag
                    style={{ cursor: "pointer" }}
                    onClick={() => props.openEmailModal(params.data)}
                  >
                    Add Email
                  </Tag>
                )}
              </div>
            );
          },
        },
      ],
    },
    {
      headerName: "Booking Details",
      children: [
        {
          headerName: "Booking Date",
          field: "email_bookingdate",
          filter: "agDateColumnFilter",
          enableRowGroup: true,
          columnGroupShow: "close",
          hide: props.serviceType === "INVOICE_FOLLOWUP",
          valueFormatter: (params) => {
            const date = parseBookingDate(params.value);
            return date ? date.toLocaleDateString("en-GB") : "";
          },
          filterParams: {
            comparator: (filterDate, cellValue) => {
              const cellDate = parseBookingDate(cellValue);
              if (!cellDate) return -1;
              const fd = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
              const cd = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
              if (cd < fd) return -1;
              if (cd > fd) return 1;
              return 0;
            },
          },
        },
        {
          headerName: "Check-in Date",
          field: "email_checkindate",
          filter: "agDateColumnFilter",
          columnGroupShow: "close",
          enableRowGroup: true,
          hide: props.serviceType === "INVOICE_FOLLOWUP",
          valueFormatter: (params) => {
            const date = parseBookingDate(params.value);
            return date ? date.toLocaleDateString("en-GB") : "";
          },
          filterParams: {
            comparator: (filterDate, cellValue) => {
              const cellDate = parseBookingDate(cellValue);
              if (!cellDate) return -1;
              const fd = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
              const cd = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
              if (cd < fd) return -1;
              if (cd > fd) return 1;
              return 0;
            },
          },
        },

        {
          headerName: props.serviceType === "INVOICE_FOLLOWUP" ? "Invoice Date" : "Check-out Date",
          field: "email_checkoutdate",
          filter: "agDateColumnFilter",
          enableRowGroup: true,
          columnGroupShow: "close",
          valueFormatter: (params) => {
            const date = parseBookingDate(params.value);
            return date ? date.toLocaleDateString("en-GB") : "";
          },
          filterParams: {
            comparator: (filterDate, cellValue) => {
              const cellDate = parseBookingDate(cellValue);
              if (!cellDate) return -1;
              const fd = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
              const cd = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
              if (cd < fd) return -1;
              if (cd > fd) return 1;
              return 0;
            },
          },
        },
        {
          headerName: "Booking ID",
          field: "email_bookingId",
          filter: "agTextColumnFilter",
          enableRowGroup: true,
          columnGroupShow: "close",
          ...(shouldMaskCustomerData
            ? {
              cellRenderer: (params) => {
                const value = params.value;
                if (!value) return null;
                return <Tag>{maskValue(value)}</Tag>;
              },
            }
            : { formatType: "CHIP" }),
        },
        {
          headerName: "Confirmation Number",
          field: "email_sharableBookingId",
          columnGroupShow: "open",
          filter: "agTextColumnFilter",
          ...(shouldMaskCustomerData
            ? {
              cellRenderer: (params) => {
                const value = params.value;
                if (!value) return null;
                return <Tag>{maskValue(value)}</Tag>;
              },
            }
            : {}),
        },
        {
          headerName: "Traveller Info",
          field: "email_travellers_info",
          filter: "agTextColumnFilter",
          columnGroupShow: "open",
          cellRenderer: (params) => {
            const travellers = params.data?.email_travellers_info;

            if (!Array.isArray(travellers) || travellers.length === 0) {
              return <span>-</span>;
            }

            return (
              <div style={{ height: "100%", width: "100%" }}>
                {travellers.map((item, index) => {
                  let parsedItem = {};

                  try {
                    parsedItem = typeof item === "string" ? JSON.parse(item) : item;
                  } catch (e) {
                    console.error("Invalid JSON:", item);
                  }

                  return (
                    <Tag key={index}>
                      {parsedItem?.name}
                    </Tag>
                  );
                })}
              </div>
            );
          },
        },


      ]
    },

    {
      headerName: "Customer Details",
      children: [
        {
          headerName: "Customer Name",
          field: "company_name",
          width: 350,
          filter: "agTextColumnFilter",
          enableRowGroup: true,
          //columnGroupShow: "open",
          cellRenderer: (params) => {
            if (params.node.group) return params.value;
            const value = params.value;
            const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
            if (!hasValue) return null;
            return (
              <div style={{ height: "100%", display: "flex", alignItems: "center" }}>
                <Tag
                  color="blue"
                  style={{ borderRadius: "5px", padding: "4px 12px", fontSize: "14px", fontWeight: 500 }}
                >
                  {shouldMaskCustomerData ? maskValue(value) : value}
                </Tag>
              </div>
            );
          },
        },

        {
          headerName: "Client GSTIN",
          field: "gstin",
          columnGroupShow: "open",
          filter: "agTextColumnFilter",
          enableRowGroup: true,
          cellRenderer: (params) => {
            // Don't render anything for grouped rows
            if (params.node.group) {
              return null; // or return "";
            }
          
            const value = params.value;
            const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
          
            return (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {hasValue ? (
                  <Tag
                    color="blue"
                    style={{
                      borderRadius: "5px",
                      padding: "4px 12px",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {shouldMaskCustomerData
                      ? maskValue(value)
                      : Array.isArray(value)
                        ? value.join(", ")
                        : value}
                  </Tag>
                ) : (
                  <Tag
                    color="red"
                    style={{
                      borderRadius: "5px",
                      padding: "4px 12px",
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    Entity Not Present in same State as hotel
                  </Tag>
                )}
              </div>
            );
          },
        },

        {
          headerName:"Client Address",
          field: "company_address",
          filter: "agTextColumnFilter",
          enableRowGroup: true,
          cellRenderer: (params) => {
            if (params.node.group) return params.value;
            const value = params.value;
            const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
            if (!hasValue) return null;
            return (
              <div style={{ height: "100%", display: "flex", alignItems: "center" }}>
                <Tag
                  color="blue"
                  style={{ borderRadius: "5px", padding: "4px 12px", fontSize: "14px", fontWeight: 500 }}
                >
                  {shouldMaskCustomerData ? maskValue(value) : value}
                </Tag>
              </div>
            );
          },
        }

      ],
    },
    {
      headerName: "Followup Status",
      children: [{
        headerName: "Follow Up Status",
        field: "followUpStatus",
        columnGroupShow: "close",
        // editable: true,
        width: 300,
        hide: true,
        editable: (params) => {
          return !params.data._pendingStatus; // 👈 disable edit if pending change
        },
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: [
            "",
            "Invoice Received",
            "Not ready to share the invoice",
            "Need booking data",
            "Hotel closed",
          ],
        },
        filter: "agSetColumnFilter",
        filterParams: {
          values: [
            "",
            "Invoice Received",
            "Not ready to share the invoice",
            "Need booking data",
            "Hotel closed",
          ],
        },
        cellRenderer: (props) => {
          const { data, value, api, node, colDef } = props;
          const hasPending = data._pendingStatus !== undefined;
          const displayValue = hasPending
            ? data._pendingStatus
            : value || "Select status";

          const handleConfirm = async (e) => {
            e.stopPropagation(); // prevent AG Grid cell click

            try {
              const res = await fetch(
                "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/updateFollowUpStatus",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    email_bookingId: data.email_bookingId,
                    followUpStatus: data._pendingStatus,
                  }),
                },
              );

              const result = await res.json();
              if (res.ok) {
                data.followUpStatus = data._pendingStatus;
                delete data._pendingStatus;
                api.refreshCells({ rowNodes: [node], columns: [colDef.field] });
              } else {
                console.error("API failed:", result);
              }
            } catch (err) {
              console.error("API error:", err);
            }
          };

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ color: !value && !hasPending ? "#aaa" : "#000" }}>
                {displayValue}
              </div>
              {hasPending && (
                <CheckCircleOutlined
                  style={{ color: "green", cursor: "pointer", marginLeft: 8 }}
                  onClick={handleConfirm}
                />
              )}
            </div>
          );
        },
        onCellValueChanged: (params) => {
          const { data, newValue, oldValue } = params;
          if (newValue !== oldValue) {
            data._pendingStatus = newValue;
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: ["followUpStatus"],
              force: true,
            });
          }
        },
      },

      {
        headerName: "Last Email Sent",
        field: "last_mail_sent_at",
        enableRowGroup: true,
        filter: "agDateColumnFilter",
        columnGroupShow: "close",
        valueFormatter: (params) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return date.toLocaleDateString("en-GB");
        },
        filterParams: {
          comparator: (filterDate, cellValue) => {
            if (!cellValue) return -1;
            const cellDate = new Date(cellValue);
            if (isNaN(cellDate.getTime())) return -1;
            const fd = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
            const cd = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
            if (cd < fd) return -1;
            if (cd > fd) return 1;
            return 0;
          },
        },
      },
      {
        headerName: "Email Updated At",
        field: "email_updated_at",
        enableRowGroup: true,
        filter: "agDateColumnFilter",
        columnGroupShow: "open",
        valueFormatter: (params) => {
          if (!params.value) return "";
          const date = new Date(params.value);
          return date.toLocaleDateString("en-GB");
        },
        filterParams: {
          comparator: (filterDate, cellValue) => {
            if (!cellValue) return -1;
            const cellDate = new Date(cellValue);
            if (isNaN(cellDate.getTime())) return -1;
            const fd = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
            const cd = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
            if (cd < fd) return -1;
            if (cd > fd) return 1;
            return 0;
          },
        },
      },
      {
        headerName: "Created At",
        field: "createdAt",
        sort: "desc",
        enableRowGroup: true,
        filter: "agDateColumnFilter",
        valueFormatter: (params) => {
          if (!params.value) return "";
          const date = new Date(Number(params.value));
          if (isNaN(date.getTime())) return "";
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        },
        filterParams: {
          comparator: (filterDate, cellValue) => {
            if (!cellValue) return -1;
            const cellDate = new Date(Number(cellValue));
            if (isNaN(cellDate.getTime())) return -1;
            const fd = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
            const cd = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
            if (cd < fd) return -1;
            if (cd > fd) return 1;
            return 0;
          },
        },
      }
        // {
        //   headerName: "Remark 1",
        //   field: "remark1",
        //   filter: "agTextColumnFilter",
        //   enableRowGroup: true,
        //   columnGroupShow: "open",
        //   editable: true,
        // },
        // {
        //   headerName: "Remark 2",
        //   field: "remark2",
        //   filter: "agTextColumnFilter",
        //   enableRowGroup: true,
        //   columnGroupShow: "open",
        //   editable: true,
        // },
      ]
    },
    // {
    //   headerName: "Invoice Details",
    //   children: [
    //     {
    //       headerName: "Invoice Received Status",
    //       field: "invoiceStatus",
    //       enableRowGroup: true,
    //       filter: "agTextColumnFilter",
    //       // filterParams: {
    //       //   values: ['Invoice Received'],
    //       // },
    //       cellRenderer: (params) => {
    //         if (params.node.group || params.value === "") return null;
    //         const value = params.value;
    //         const isReceived = value === "Invoice Received";
    //         return (
    //           <Tag color={isReceived ? "green" : "red"}>
    //             {isReceived ? "Invoice Received" : "Invoice Not Received"}
    //           </Tag>
    //         );
    //       },
    //     },
    //     {
    //       headerName: "View Invoice",
    //       field: "invoice_link_new",
    //       cellRenderer: (params) => {
    //         let links = [];

    //         if (typeof params.value === "string") {
    //           links = params.value
    //             .split(",")
    //             .map((link) => link.trim())
    //             .filter((link) => !!link);
    //         } else if (Array.isArray(params.value)) {
    //           links = params.value
    //             .map((link) => (typeof link === "string" ? link.trim() : ""))
    //             .filter((link) => !!link);
    //         }

    //         const handleClick = () => {
    //           if (links.length > 0) {
    //             handleInvoiceView(links);
    //           }
    //         };

    //         return links.length > 0 ? (
    //           <span
    //             onClick={handleClick}
    //             style={{ cursor: "pointer", color: "blue" }}
    //           >
    //             View Invoice
    //           </span>
    //         ) : (
    //           <span style={{ color: "#888" }}>No Invoice</span>
    //         );
    //       },
    //     },

    //   ]
    // },
    {
      headerName: "Booking Client ID",
      field: "booking_client_id",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      hide: true,
    },


    {
      headerName: "Template",
      width: 250,
      filter: false,
      cellRenderer: (params) => {
        if (params.node.group || !params.data) return null;

        const clientConfig = props.clientNamesMapRef?.current?.[params.data.booking_client_id];
        const templates = clientConfig?.templates;

        if (!templates || templates.length === 0) return null;

        return (
          <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
            {templates.map((template, index) => (
              <Tooltip key={index} title={template.interval}>
                <div
                  onClick={() => {
                    props.setModalType("TEMPLATE");
                    props.setModalData({ ...params.data, selectedTemplateId: template.templateId, selectedInterval: template.interval });
                    props.setIsModalVisible(true);
                  }}
                  style={{
                    backgroundColor: "#2D3748",
                    color: "white",
                    cursor: "pointer",
                    borderRadius: "50%",
                    width: 18,
                    height: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 10,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </div>
              </Tooltip>
            ))}
          </div>
        );
      },
    },



    {
      headerName: "Pincode",
      field: "pincode",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      editable: true,
      hide: true,
    },


    {
      headerName: "Hotel Contact Number",
      field: "email_hotel_contact_phone",
      width: 300,
      hide: true,
      enableRowGroup: true,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        if (params.node.group) {
          return null;
        }
        const phoneNumbers = params.value; // Assuming it's an array of phone numbers
        const visiblePhone = phoneNumbers?.[0]; // Show only the first phone number
        const remainingCount =
          phoneNumbers?.length > 1 ? phoneNumbers.length - 1 : 0; // Count remaining numbers

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              flexWrap: "wrap",
              overflow: "hidden",
              maxWidth: "100%",
            }}
          >
            {phoneNumbers &&
              Array.isArray(phoneNumbers) &&
              phoneNumbers.length > 0 ? (
              <>
                <Tag icon={<PhoneOutlined />}>{visiblePhone}</Tag>

                {remainingCount > 0 && (
                  <Tooltip
                    title={phoneNumbers.slice(1).map((phone, idx) => (
                      <div key={idx}>{phone}</div>
                    ))}
                  >
                    <span style={{ color: "#1890ff", cursor: "pointer" }}>
                      +{remainingCount}
                    </span>
                  </Tooltip>
                )}

                <EditFilled
                  style={{ cursor: "pointer", marginLeft: "5px" }}
                  onClick={() => props?.openPhoneModal(params.data)}
                />
              </>
            ) : (
              <Tag
                style={{ cursor: "pointer" }}
                onClick={() => props?.openPhoneModal(params.data)}
              >
                Add Phone
              </Tag>
            )}
          </div>
        );
      },
    },


    {
      headerName: "GST Amount",
      field: "totalGST",
      filter: "agNumberColumnFilter",
      hide: true
      // enableRowGroup: true,
      // filter: "agNumberColumnFilter",
      // valueFormatter: (params) => {
      //   const value = params.value;
      //   return value == null || value === "" ? "-" : value;
      // },
      // cellRenderer: (params) => {
      //   if (params.node.group || !params.data) return null;

      //   const value = params.value;
      //   return value == null || value === "" ? "-" : value;
      // },
    },






    {
      headerName: "Current Followup Stage",
      field: "last_mail_type",
      enableRowGroup: true,
      filter: "agTextColumnFilter",
    },


    ...(props.serviceType === "INVOICE_FOLLOWUP"
      ? [
        {
          headerName: "Email Logs",
          field: "email_logs",
          enableRowGroup: true,
          filter: false,
          width: 350,
          cellRenderer: (params) => {
            if (params.node.group || params.value === "") return null;
            return handleRenderEmailEvents(params.value);
          },
        },
      ]
      : [
        {
          headerName: "Hotel Email Logs",
          field: "email_logs",
          colId: "hotel_email_logs",
          enableRowGroup: false,
          filter: false,
          width: 200,
          cellRenderer: (params) => {
            if (params.node.group) return null;
            return renderSegmentedLogSummary(
              params.value,
              params.data?.email_bookingdate,
              HOTEL_LOG_TYPES,
              params.data?.createdAt,
              params.data?.email_checkindate,
              params.data?.email_checkoutdate,
            );
          },
        },
        {
          headerName: "Traveller Email Logs",
          field: "email_logs",
          colId: "traveller_email_logs",
          enableRowGroup: false,
          filter: false,
          width: 200,
          cellRenderer: (params) => {
            if (params.node.group) return null;
            return renderSegmentedLogSummary(
              params.value,
              params.data?.email_bookingdate,
              TRAVELLER_LOG_TYPES,
              params.data?.createdAt,
              params.data?.email_checkindate,
              params.data?.email_checkoutdate,
            );
          },
        },
        // Hidden export-only columns: one per log type so the download shows a
        // separate "First Mail" date/time column instead of one combined cell.
        {
          headerName: "Hotel Booking First Mail",
          field: "email_logs",
          colId: "hotel_first_mail_booking",
          hide: true,
          filter: false,
          valueGetter: (params) => getFirstMailForLogType(params.data?.email_logs, "BOOKING_HOTEL"),
        },
        {
          headerName: "Hotel Checkin First Mail",
          field: "email_logs",
          colId: "hotel_first_mail_checkin",
          hide: true,
          filter: false,
          valueGetter: (params) => getFirstMailForLogType(params.data?.email_logs, "CHECKIN_HOTEL"),
        },
        {
          headerName: "Hotel Before Checkout First Mail",
          field: "email_logs",
          colId: "hotel_first_mail_before_checkout",
          hide: true,
          filter: false,
          valueGetter: (params) => getFirstMailForLogType(params.data?.email_logs, "BEFORE_CHECKOUT_HOTEL"),
        },
        {
          headerName: "Hotel After Checkout First Mail",
          field: "email_logs",
          colId: "hotel_first_mail_after_checkout",
          hide: true,
          filter: false,
          valueGetter: (params) => getFirstMailForLogType(params.data?.email_logs, "AFTER_CHECKOUT_HOTEL"),
        },
        {
          headerName: "Traveller Prior Checkin First Mail",
          field: "email_logs",
          colId: "traveller_first_mail_prior_checkin",
          hide: true,
          filter: false,
          valueGetter: (params) => getFirstMailForLogType(params.data?.email_logs, "TO_TRAVELLER_PRIOR_CHECKIN"),
        },
        {
          headerName: "Traveller On Checkin Day First Mail",
          field: "email_logs",
          colId: "traveller_first_mail_checkin_day",
          hide: true,
          filter: false,
          valueGetter: (params) => getFirstMailForLogType(params.data?.email_logs, "TO_TRAVELLER_ON_CHECKIN_DAY"),
        },
        {
          headerName: "Traveller Before Checkout First Mail",
          field: "email_logs",
          colId: "traveller_first_mail_before_checkout",
          hide: true,
          filter: false,
          valueGetter: (params) => getFirstMailForLogType(params.data?.email_logs, "TO_TRAVELLER_BEFORE_CHECKOUT"),
        },
      ]),

    ...(props.isDevApp ? [{
      headerName: "Manual Email Logs",
      field: "email_logs",
      colId: "manual_email_logs",
      enableRowGroup: false,
      filter: false,
      width: 200,
      cellRenderer: (params) => {
        if (params.node.group) return null;
        return renderManualLogSummary(params.value);
      },
    }] : []),

    {
      headerName: "Action",
      enableRowGroup: true,
      cellRenderer: (params) => {
        if (params.node.group || params.value === "") return null;

        return (
          <Button
            icon={<MailOutlined />}
            size="small"
            onClick={() => {
              if (props.openResendModal) {
                props.openResendModal(params.data);
              }
            }}
          >
            Send Single Mail
          </Button>
        );
      },
    },
    {
      headerName: "Mail Config Status",
      field: "mailConfigStauts",
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["RUNNING", "EMAILREQUIRED", "EMAIL_REQUIRED"],
      },
      enableRowGroup: true,
      cellRenderer: (params) => {
        if (params.node.group || !params?.data?.mailConfigStauts) return null;

        const status = params.data.mailConfigStauts;
        const color = status === "RUNNING" ? "green" : "red";

        return <Tag color={color}>{status}</Tag>;
      },
    },


    // {
    //   headerName: "GST Amount",
    //   field: "total_gst",
    //   filter: "agNumberColumnFilter",
    //   valueFormatter: (params) => {
    //     const value = params.value;
    //     return !value ? '-' : value;
    //   }
    // },
    ...(props.isDevApp ? [{
      field: "score",
      headerName: "Email Score",
      filter: "agNumberColumnFilter",
    }] : []),
  ];
};

export const AIRLINE_FOLLOWUP_COLUMN_DEFS = (props) => {
  return [
    {
      headerName: "Followup completed",
      field: "followup_completed",
    },
    {
      headerName: "Tracking Id",
      field: "trackingId",
      hide: true,
    },
    {
      headerName: "Select",
      field: "id", // or any key
      checkboxSelection: (params) => {
        // Only show checkbox for child rows under a group
        return !params.node.group && params.node.parent != null;
      },
      headerCheckboxSelection: false,
      width: 100,
      pinned: "left",
    },
    {
      headerName: "Mail Config Status",
      field: "mailConfigStauts",
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["RUNNING", "EMAILREQUIRED"],
      },
      enableRowGroup: true,
      cellRenderer: (params) => {
        if (params.node.group || !params?.data?.mailConfigStauts) return null;

        const status = params.data.mailConfigStauts;
        const color = status === "RUNNING" ? "green" : "red";

        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      headerName: "Booking ID/Invoice Number",
      field: "email_bookingId",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
    },
    {
      headerName: "Booking Client ID",
      field: "booking_client_id",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
    },

    // {
    //   headerName: "Client Name",
    //   field: "company_name",
    //   filter: "agTextColumnFilter",
    //   enableRowGroup: true,
    //   // rowGroupIndex: 1,
    // },
    {
      headerName: "Agency Name",
      field: "agency_name",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      // rowGroupIndex: 1,
    },
    {
      headerName: "Workspace",
      field: "company_name",
      enableRowGroup: true,
    },
    {
      headerName: "Template",
      width: 200,
      cellRenderer: (params) => {
        if (params.node.group || !params.data) return null;

        return (
          <Button
            type="primary"
            onClick={() => {
              props.setModalType("TEMPLATE");
              props.setModalData(params.data);
              props.setIsModalVisible(true);
            }}
            size="small"
          >
            Show Template
          </Button>
        );
      },
    },

    {
      headerName: "Airline Contact Email",
      field: "email_hotel_contact_email",
      enableRowGroup: true,
      width: 300,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        if (params.node.group) {
          return null;
        }
        const emails = params.value; // Assuming params.value is an array of emails
        const visibleEmail = emails?.[0]; // Show only the first email
        const remainingCount = emails?.length > 1 ? emails.length - 1 : 0; // Count remaining emails

        return (
          <div
            style={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              flexWrap: "wrap",
            }}
          >
            {emails && Array.isArray(emails) && emails.length > 0 ? (
              <>
                <Tag icon={<MailOutlined />}>{visibleEmail}</Tag>

                {remainingCount > 0 && (
                  <Tooltip
                    title={emails.slice(1).map((email, idx) => (
                      <div key={idx}>{email}</div>
                    ))}
                  >
                    <span style={{ color: "#1890ff", cursor: "pointer" }}>
                      +{remainingCount}
                    </span>
                  </Tooltip>
                )}

                <EditFilled
                  style={{ cursor: "pointer", marginLeft: "5px" }}
                  onClick={() => props.openEmailModal(params.data)}
                />
              </>
            ) : (
              <Tag
                style={{ cursor: "pointer" }}
                onClick={() => props.openEmailModal(params.data)}
              >
                Add Email
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      headerName: "SSR Email",
      field: "stuck_email_address",
      enableRowGroup: true,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Airline Name",
      field: "airline_name",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      CheckBoxSelection: true,
      // rowGroupIndex: 0,
    },
    {
      headerName: "GST Amount",
      field: "totalGST",
      filter: "agNumberColumnFilter",
      // enableRowGroup: true,
      // filter: "agNumberColumnFilter",
      // valueFormatter: (params) => {
      //   const value = params.value;
      //   return value == null || value === "" ? "-" : value;
      // },
      // cellRenderer: (params) => {
      //   if (params.node.group || !params.data) return null;

      //   const value = params.value;
      //   return value == null || value === "" ? "-" : value;
      // },
    },
    // {
    //   headerName: "Transaction Date",
    //   field: "transaction_date",
    //   filter: "agDateColumnFilter",
    //   enableRowGroup: true,
    //   valueFormatter: (params) => {
    //     if (!params.value) return "";
    //     const date = new Date(params.value);
    //     return date.toLocaleDateString("en-GB"); // Formats as "dd/mm/yyyy"
    //   },
    // },
    {
      headerName: "Transaction Date",
      field: "transaction_date",
      filter: "agDateColumnFilter",
      enableRowGroup: true,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);

        if (isNaN(date.getTime())) return params.value; // Handle invalid date safely

        return date.toLocaleDateString("en-GB"); // ✅ dd/mm/yyyy
      },
    },
    {
      headerName: "Invoice/Travel Date",
      field: "invoice_date",
      filter: "agDateColumnFilter",
      enableRowGroup: true,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        return date.toLocaleDateString("en-GB"); // Formats as "dd/mm/yyyy"
      },
    },

    {
      headerName: "Current Followup Stage",
      field: "last_mail_type",
      enableRowGroup: true,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Last Email Sent",
      field: "last_mail_sent_at",
      enableRowGroup: true,
      filter: "agDateColumnFilter",
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = new Date(params.value);
        return date.toLocaleDateString("en-GB"); // Formats as "dd/mm/yyyy"
      },
    },

    {
      headerName: "Email Logs",
      field: "email_logs",
      enableRowGroup: true,
      cellRenderer: (params) => {
        if (params.node.group || params.value === "") return null;

        return handleRenderEmailEvents(params.value);
      },
    },

    {
      headerName: "Action",
      enableRowGroup: true,
      cellRenderer: (params) => {
        if (params.node.group || params.value === "") return null;

        return (
          <Button
            icon={<MailOutlined />}
            size="small"
            onClick={() => {
              // Resend mail handler
            }}
          >
            Send Next Followup Mail
          </Button>
        );
      },
    },

    {
      headerName: "View Invoice",
      field: "invoice_link_new",
      cellRenderer: (params) => {
        let links = [];

        if (typeof params.value === "string") {
          links = params.value
            .split(",")
            .map((link) => link.trim())
            .filter((link) => !!link);
        } else if (Array.isArray(params.value)) {
          links = params.value
            .map((link) => (typeof link === "string" ? link.trim() : ""))
            .filter((link) => !!link);
        }

        const handleClick = () => {
          if (links.length > 0) {
            handleInvoiceView(links);
          }
        };

        return links.length > 0 ? (
          <span
            onClick={handleClick}
            style={{ cursor: "pointer", color: "blue" }}
          >
            View Invoice
          </span>
        ) : (
          <span style={{ color: "#888" }}>No Invoice</span>
        );
      },
    },
    {
      headerName: "User Reply",
      field: "userReply",
    },
    {
      headerName: "Reply Context",
      field: "replyContext",
      filter: "agSetColumnFilter",
      filterParams: {
        values: [
          "Ticket Created",
          "Invalid Booking Data",
          "Invoice Already Shared",
        ],
      },
      cellRenderer: (params) => {
        if (params.node.group || !params.value) return null;

        const value = params.value;
        let color = "default";

        switch (value) {
          case "Ticket Created":
            color = "gold"; // yellow
            break;
          case "Invalid Booking Data":
            color = "red";
            break;
          case "Invoice Already Shared":
            color = "blue";
            break;
          default:
            color = "default"; // fallback for unknown values
        }

        return <Tag color={color}>{value}</Tag>;
      },
    },
    {
      headerName: "Reply At",
      field: "reply_at",
      filter: "agDateColumnFilter",
      enableRowGroup: true,

      valueFormatter: (params) => {
        if (!params.value) return "";

        let date;

        // Case 1: DD-MM-YYYY
        if (/^\d{2}-\d{2}-\d{4}$/.test(params.value)) {
          const [day, month, year] = params.value.split("-");
          date = new Date(`${year}-${month}-${day}`);
        }
        // Case 2: ISO string (e.g., 2025-09-20T11:00:00+05:30)
        else {
          date = new Date(params.value);
        }

        if (isNaN(date.getTime())) return "Invalid Date";

        return date.toLocaleDateString("en-GB"); // dd/mm/yyyy
      },

      filterParams: {
        comparator: (filterDate, cellValue) => {
          if (!cellValue) return -1;

          let date;

          // Case 1: DD-MM-YYYY
          if (/^\d{2}-\d{2}-\d{4}$/.test(cellValue)) {
            const [day, month, year] = cellValue.split("-");
            date = new Date(`${year}-${month}-${day}`);
          }
          // Case 2: ISO string
          else {
            date = new Date(cellValue);
          }

          if (isNaN(date.getTime())) return -1;

          if (date < filterDate) return -1;
          if (date > filterDate) return 1;
          return 0;
        },
      },
    },
    {
      headerName: "Invoice Received Status",
      field: "invoiceStatus",
      enableRowGroup: true,
      filter: "agTextColumnFilter",
      // filterParams: {
      //   values: ['Invoice Received'],
      // },
      cellRenderer: (params) => {
        if (params.node.group || params.value === "") return null;
        const value = params.value;
        const isReceived = value === "Invoice Received";
        return (
          <Tag color={isReceived ? "green" : "red"}>
            {isReceived ? "Invoice Received" : "Invoice Not Received"}
          </Tag>
        );
      },
    },
    // {
    //   headerName: "GST Amount",
    //   field: "total_gst",
    //   filter: "agNumberColumnFilter",
    //   valueFormatter: (params) => {
    //     const value = params.value;
    //     return !value ? '-' : value;
    //   }
    // },
    {
      headerName: "GSTIN Match Status",
      field: "gstMatchStatus",
      enableRowGroup: true,
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["MATCH", "MISMATCH"],
      },
      cellRenderer: (params) => {
        if (params.node.group) return null;
        const value = params.value?.toUpperCase();
        if (value === "MATCH") {
          return (
            <Tag color="green" style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: "8px", padding: "4px 10px", fontWeight: 500 }}>
              <CheckCircleFilled style={{ fontSize: "14px" }} /> MATCH
            </Tag>
          );
        } else if (value === "MISMATCH") {
          return (
            <Tag color="red" style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: "8px", padding: "4px 10px", fontWeight: 500 }}>
              <CloseCircleFilled style={{ fontSize: "14px" }} /> MISMATCH
            </Tag>
          );
        }
        return null;
      },
    },
    {
      headerName: "Follow Up Status",
      field: "followUpStatus",
      editable: true,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: [
          "",
          "Invoice Received",
          "Not ready to share the invoice",
          "Need booking data",
          "Hotel closed",
        ],
      },
      filter: "agSetColumnFilter",
      filterParams: {
        values: [
          "", // represents "Select status" or blank
          "Invoice Received",
          "Not ready to share the invoice",
          "Need booking data",
          "Hotel closed",
        ],
      },
      cellRenderer: (props) => {
        const { data, value, api, node, colDef } = props;

        const hasPending = data._pendingStatus !== undefined;

        const handleConfirm = async () => {
          const email_bookingId = data.email_bookingId;
          const followUpStatus = data._pendingStatus;

          try {
            const res = await fetch(
              "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/updateFollowUpStatus",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email_bookingId, followUpStatus }),
              },
            );

            // const result = await res.json();

            if (res.ok) {
              data.followUpStatus = followUpStatus;
              delete data._pendingStatus;
              api.refreshCells({ rowNodes: [node], columns: [colDef.field] });
            }
          } catch (err) {
            console.error("API error:", err);
          }
        };


        const displayValue = hasPending
          ? data._pendingStatus
          : value || "Select status";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div style={{ color: !value ? "#aaa" : "#000" }}>
              {displayValue}
            </div>
            {hasPending && (
              <>
                <div
                  style={{ color: "grey", cursor: "pointer" }}
                  onClick={handleConfirm}
                >
                  <CheckCircleOutlined />
                </div>
              </>
            )}
          </div>
        );
      },
      onCellValueChanged: (params) => {
        const { data, newValue, oldValue } = params;
        if (newValue !== oldValue) {
          data._pendingStatus = newValue;
          data.followUpStatus = oldValue; // Temporarily revert
          params.api.refreshCells({
            rowNodes: [params.node],
            columns: ["followUpStatus"],
          });
        }
      },
    },
  ];
};

export const CLIENT_FOLLOWUP_COLUMN_DEFS = (props) => {
  return [
    {
      headerName: "Tracking Id",
      field: "trackingId",
      hide: true,
    },
    {
      headerName: "Select",
      field: "id", // or any key
      checkboxSelection: (params) => {
        // Only show checkbox for child rows under a group
        return !params.node.group && params.node.parent != null;
      },
      headerCheckboxSelection: false,
      width: 100,
      pinned: "left",
    },
    {
      headerName: "contactName",
      field: "contactName",
    },
    {
      headerName: "Mail Config Status",
      field: "mailConfigStauts",
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["RUNNING", "EMAILREQUIRED"],
      },
      enableRowGroup: true,
      cellRenderer: (params) => {
        if (params.node.group || !params?.data?.mailConfigStauts) return null;

        const status = params.data.mailConfigStauts;
        const color = status === "RUNNING" ? "green" : "red";

        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      headerName: "Booking ID",
      field: "email_bookingId",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
    },
    {
      headerName: "Booking Client ID",
      field: "booking_client_id",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      hide: true,
    },

    // {
    //   headerName: "Client Name",
    //   field: "company_name",
    //   filter: "agTextColumnFilter",
    //   enableRowGroup: true,
    //   // rowGroupIndex: 1,
    // },

    {
      headerName: "Workspace",
      field: "company_name",
      enableRowGroup: true,
    },
    {
      headerName: "Template",
      width: 200,
      cellRenderer: (params) => {
        if (params.node.group || !params.data) return null;

        return (
          <Button
            type="primary"
            onClick={() => {
              props.setModalType("TEMPLATE");
              props.setModalData(params.data);
              props.setIsModalVisible(true);
            }}
            size="small"
          >
            Show Template
          </Button>
        );
      },
    },

    {
      headerName: "Client Contact Email",
      field: "email_hotel_contact_email",
      enableRowGroup: true,
      width: 300,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        if (params.node.group) {
          return null;
        }
        const emails = params.value; // Assuming params.value is an array of emails
        const visibleEmail = emails?.[0]; // Show only the first email
        const remainingCount = emails?.length > 1 ? emails.length - 1 : 0; // Count remaining emails

        return (
          <div
            style={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: "5px",
              flexWrap: "wrap",
            }}
          >
            {emails && Array.isArray(emails) && emails.length > 0 ? (
              <>
                <Tag icon={<MailOutlined />}>{visibleEmail}</Tag>

                {remainingCount > 0 && (
                  <Tooltip
                    title={emails.slice(1).map((email, idx) => (
                      <div key={idx}>{email}</div>
                    ))}
                  >
                    <span style={{ color: "#1890ff", cursor: "pointer" }}>
                      +{remainingCount}
                    </span>
                  </Tooltip>
                )}

                <EditFilled
                  style={{ cursor: "pointer", marginLeft: "5px" }}
                  onClick={() => props.openEmailModal(params.data)}
                />
              </>
            ) : (
              <Tag
                style={{ cursor: "pointer" }}
                onClick={() => props.openEmailModal(params.data)}
              >
                Add Email
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      field: "totalAtRisk",
      headerName: "TotalAtRisk",
    },
    {
      headernamwe: "Total K3",
      field: "totalK3",
    },
    {
      field: "totalReceived",
      headerName: "Total Received",
    },

    {
      headerName: "Current Followup Stage",
      field: "last_mail_type",
      enableRowGroup: true,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Last Email Sent",
      field: "last_mail_sent_at",
      enableRowGroup: true,
      filter: "agDateColumnFilter",
      cellRenderer: (params) => {
        if (params.node.group) return null;

        if (params.value) {
          const date = new Date(params.value);
          return <span>{date.toLocaleDateString("en-GB")}</span>;
        }

        const logs = parseLogs(params.data?.email_logs);
        const earliestTs = getEarliestSendDate(logs);
        if (earliestTs) {
          return (
            <span style={{ color: "#888" }}>
              {moment.unix(earliestTs).format("DD/MM/YYYY HH:mm")}
            </span>
          );
        }

        return null;
      },
    },

    {
      headerName: "Email Logs",
      field: "email_logs",
      enableRowGroup: true,
      cellRenderer: (params) => {
        if (params.node.group || params.value === "") return null;

        return handleRenderEmailEvents(params.value);
      },
    },

    {
      headerName: "Action",
      enableRowGroup: true,
      cellRenderer: (params) => {
        if (params.node.group || params.value === "") return null;

        return (
          <Button
            icon={<MailOutlined />}
            size="small"
            onClick={() => {
              // Resend mail handler
            }}
          >
            Send Next Followup Mail
          </Button>
        );
      },
    },

    {
      headerName: "View Invoice",
      field: "invoice_link_new",
      cellRenderer: (params) => {
        let links = [];

        if (typeof params.value === "string") {
          links = params.value
            .split(",")
            .map((link) => link.trim())
            .filter((link) => !!link);
        } else if (Array.isArray(params.value)) {
          links = params.value
            .map((link) => (typeof link === "string" ? link.trim() : ""))
            .filter((link) => !!link);
        }

        const handleClick = () => {
          if (links.length > 0) {
            handleInvoiceView(links);
          }
        };

        return links.length > 0 ? (
          <span
            onClick={handleClick}
            style={{ cursor: "pointer", color: "blue" }}
          >
            View Invoice
          </span>
        ) : (
          <span style={{ color: "#888" }}>No Invoice</span>
        );
      },
    },

    {
      headerName: "Invoice Received Status",
      field: "invoiceStatus",
      enableRowGroup: true,
      filter: "agTextColumnFilter",
      // filterParams: {
      //   values: ['Invoice Received'],
      // },
      cellRenderer: (params) => {
        if (params.node.group || params.value === "") return null;
        const value = params.value;
        const isReceived = value === "Invoice Received";
        return (
          <Tag color={isReceived ? "green" : "red"}>
            {isReceived ? "Invoice Received" : "Invoice Not Received"}
          </Tag>
        );
      },
    },
    // {
    //   headerName: "GST Amount",
    //   field: "total_gst",
    //   filter: "agNumberColumnFilter",
    //   valueFormatter: (params) => {
    //     const value = params.value;
    //     return !value ? '-' : value;
    //   }
    // },
    {
      headerName: "GST Amount",
      field: "totalGST",
      filter: "agNumberColumnFilter",
      // enableRowGroup: true,
      // filter: "agNumberColumnFilter",
      // valueFormatter: (params) => {
      //   const value = params.value;
      //   return value == null || value === "" ? "-" : value;
      // },
      // cellRenderer: (params) => {
      //   if (params.node.group || !params.data) return null;

      //   const value = params.value;
      //   return value == null || value === "" ? "-" : value;
      // },
    },

    {
      headerName: "GSTIN Match Status",
      field: "gstMatchStatus",
      enableRowGroup: true,
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["MATCH", "MISMATCH"],
      },
      cellRenderer: (params) => {
        if (params.node.group) return null;
        const value = params.value?.toUpperCase();
        if (value === "MATCH") {
          return (
            <Tag color="green" style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: "8px", padding: "4px 10px", fontWeight: 500 }}>
              <CheckCircleFilled style={{ fontSize: "14px" }} /> MATCH
            </Tag>
          );
        } else if (value === "MISMATCH") {
          return (
            <Tag color="red" style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: "8px", padding: "4px 10px", fontWeight: 500 }}>
              <CloseCircleFilled style={{ fontSize: "14px" }} /> MISMATCH
            </Tag>
          );
        }
        return null;
      },
    },
    {
      headerName: "Email should be sent",
      field: "emailShouldBeSent", // new field name
      // editable: true,
      editable: (params) => {
        return !params.data._pendingEmailStatus; // 👈 disable edit if pending change
      },
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: [
          " ", // optional initial blank
          "Yes, Send the mail",
          "No, don't send the mail",
        ],
      },
      filter: "agSetColumnFilter",
      filterParams: {
        values: [" ", "Yes, Send the mail", "No, don't send the mail"],
      },
      cellRenderer: (props) => {
        const { data, value, api, node, colDef } = props;

        const hasPending = data._pendingEmailStatus !== undefined;

        const handleConfirm = async () => {
          const email_bookingId = data.email_bookingId;
          const emailShouldBeSent = data._pendingEmailStatus ?? "";

          try {
            const res = await fetch(
              "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/updateFollowUpStatus",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email_bookingId, emailShouldBeSent }),
              },
            );

            // const result = await res.json();

            if (res.ok) {
              data.emailShouldBeSent = emailShouldBeSent;
              delete data._pendingEmailStatus;
              api.refreshCells({ rowNodes: [node], columns: [colDef.field] });
              api.redrawRows({ rowNodes: [node] });
            }
          } catch (err) {
            console.error("API error:", err);
          }
        };


        const displayValue = hasPending
          ? data._pendingEmailStatus
          : value || "Select option";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div style={{ color: !value ? "#aaa" : "#000" }}>
              {displayValue}
            </div>
            {hasPending && (
              <div
                style={{ color: "green", cursor: "pointer" }}
                onClick={(e) => {
                  e.preventDefault(); // prevent AG Grid's internal cell edit behavior
                  e.stopPropagation(); // stop grid from swallowing the click
                  handleConfirm(); // your actual API call
                }}
              >
                <CheckCircleOutlined />
              </div>
            )}
          </div>
        );
      },
      onCellValueChanged: (params) => {
        const { data, newValue, oldValue } = params;

        if (newValue !== oldValue || (newValue === " " && oldValue)) {
          {
            data._pendingEmailStatus = newValue;
            // data.emailShouldBeSent = oldValue; // Temporarily revert
            params.api.refreshCells({
              rowNodes: [params.node],
              columns: ["emailShouldBeSent"],
              force: true,
            });
          }
        }
      },
    },

    {
      headerName: "Follow Up Status",
      field: "followUpStatus",
      // editable: true,
      editable: (params) => {
        return !params.data._pendingStatus; // 👈 disable edit if pending change
      },
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: [
          "",
          "Invoice Received",
          "Not ready to share the invoice",
          "Need booking data",
          "Hotel closed",
        ],
      },
      filter: "agSetColumnFilter",
      filterParams: {
        values: [
          "",
          "Invoice Received",
          "Not ready to share the invoice",
          "Need booking data",
          "Hotel closed",
        ],
      },
      cellRenderer: (props) => {
        const { data, value, api, node, colDef } = props;
        const hasPending = data._pendingStatus !== undefined;
        const displayValue = hasPending
          ? data._pendingStatus
          : value || "Select status";

        const handleConfirm = async (e) => {
          e.stopPropagation(); // prevent AG Grid cell click

          try {
            const res = await fetch(
              "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/updateFollowUpStatus",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email_bookingId: data.email_bookingId,
                  followUpStatus: data._pendingStatus,
                }),
              },
            );

            const result = await res.json();
            if (res.ok) {
              data.followUpStatus = data._pendingStatus;
              delete data._pendingStatus;
              api.refreshCells({ rowNodes: [node], columns: [colDef.field] });
            } else {
              console.error("API failed:", result);
            }
          } catch (err) {
            console.error("API error:", err);
          }
        };

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div style={{ color: !value && !hasPending ? "#aaa" : "#000" }}>
              {displayValue}
            </div>
            {hasPending && (
              <CheckCircleOutlined
                style={{ color: "green", cursor: "pointer", marginLeft: 8 }}
                onClick={handleConfirm}
              />
            )}
          </div>
        );
      },
      onCellValueChanged: (params) => {
        const { data, newValue, oldValue } = params;
        if (newValue !== oldValue) {
          data._pendingStatus = newValue;
          params.api.refreshCells({
            rowNodes: [params.node],
            columns: ["followUpStatus"],
            force: true,
          });
        }
      },
    },
  ];
};
